import { Badge, Checkbox, cn } from '@solinkcorp/solink-ui';
import { type EventCamera } from 'clients/PlatformApi';
import S3Image from 'components/S3Image/S3Image';
import { MapPinned, Video } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import useLocale from 'src/hooks/useLocale';
import { type VideoDiscoverySite } from 'utils/types';

type CameraState = EventCamera & { checked: boolean };

interface PrintThumbnailsAspenProps {
  location: VideoDiscoverySite;
  eventCameras: EventCamera[];
}

function CameraBadge({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <Badge
      variant="video"
      // Browsers drop background colours in print unless the badge asks to keep them.
      className="max-w-full gap-1.5 [print-color-adjust:exact]"
    >
      <span className="shrink-0">{icon}</span>
      <span className="truncate">{text}</span>
    </Badge>
  );
}

export function PrintThumbnailsAspen({ location, eventCameras }: PrintThumbnailsAspenProps) {
  const locale = useLocale();
  const [cameras, setCameras] = useState<CameraState[]>(() =>
    eventCameras.map((camera) => ({ ...camera, checked: true })),
  );

  const toggleCamera = (id: string) => {
    setCameras((current) =>
      current.map((camera) => {
        const chCameraId = findCameraCallhomeId(location, camera);
        if (chCameraId === id) {
          return { ...camera, checked: !camera.checked };
        }
        return camera;
      }),
    );
  };

  const checkedCount = cameras.filter((camera) => camera.checked).length;

  return (
    <div
      data-testid="thumbnails"
      className={cn(
        'grid gap-3',
        cameras.length > 1 && 'sm:grid-cols-2',
        checkedCount > 1 ? 'print:grid-cols-2' : 'print:grid-cols-1',
      )}
    >
      {cameras.map((camera) => {
        const chCameraId = findCameraCallhomeId(location, camera);
        if (!chCameraId) {
          return null;
        }

        return (
          <figure
            key={chCameraId}
            className={cn(
              'relative aspect-video w-full break-inside-avoid overflow-hidden rounded-lg bg-background-elevated',
              !camera.checked && 'print:hidden',
            )}
          >
            <S3Image
              id={chCameraId}
              url={camera.thumbnail}
              className={cn(
                'absolute inset-0 transition-[filter] duration-200',
                !camera.checked && 'grayscale',
              )}
              forceAspectRatio={false}
              draggable={false}
            />
            <div
              aria-hidden
              className={cn(
                'pointer-events-none absolute inset-0 z-10 transition-opacity duration-200 bg-black/50',
                camera.checked ? 'opacity-0' : 'opacity-100',
              )}
            />
            <label
              htmlFor={chCameraId}
              className="dark absolute right-2 top-2 z-20 flex cursor-pointer items-center gap-2 rounded-md py-1 pl-1.5 pr-2.5 text-white bg-black/70 body-sm print:hidden"
            >
              <Checkbox
                id={chCameraId}
                checked={camera.checked}
                onCheckedChange={() => toggleCamera(chCameraId)}
              />
              {locale.print.includeImage}
            </label>
            <figcaption className="absolute inset-x-2 bottom-2 z-20 flex flex-wrap items-center gap-1.5">
              <CameraBadge icon={<Video className="size-3.5" aria-hidden />} text={camera.name} />
              {location.name ? (
                <CameraBadge
                  icon={<MapPinned className="size-3.5" aria-hidden />}
                  text={location.name}
                />
              ) : null}
            </figcaption>
          </figure>
        );
      })}
    </div>
  );
}

function findCameraCallhomeId(location: VideoDiscoverySite, eventCamera: EventCamera) {
  if (eventCamera.chId) {
    return eventCamera.chId;
  }
  return location.cameras.find((locationCamera) => locationCamera.cameraId === eventCamera.id)?.id;
}
